import {DatePipe} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {filter, skip, Subject, takeUntil} from 'rxjs';

import {DnbUsageStatus} from '../../../../models/dnb-usage.model';
import {MaterialModule} from '../../../../material.module';
import {ClientService} from '../../../../services/auth/client.service';
import {ClientStateService} from '../../../../services/auth/client-state.service';
import {DnbService} from '../../../../services/enrich/dnb.service';
import {EnrichConsoleDiagnosticsService} from '../../../../services/enrich/enrich-console-diagnostics.service';

@Component({
  selector: 'app-dnb-usage-card',
  standalone: true,
  imports: [MaterialModule, DatePipe],
  templateUrl: './dnb-usage-card.component.html',
  styleUrl: './dnb-usage-card.component.scss',
})
export class DnbUsageCardComponent implements OnInit, OnDestroy {
  private readonly clientService = inject(ClientService);
  private readonly clientStateService = inject(ClientStateService);
  private readonly dnbService = inject(DnbService);
  private readonly diagnostics = inject(EnrichConsoleDiagnosticsService);
  private readonly destroyed$ = new Subject<void>();
  private requestGeneration = 0;

  status: DnbUsageStatus | null = null;
  isLoading = false;
  error: string | null = null;

  ngOnInit(): void {
    this.clientStateService.resetCache$.pipe(
      skip(1),
      filter((reset) => reset),
      takeUntil(this.destroyed$),
    ).subscribe(() => this.refresh());
    this.refresh();
  }

  ngOnDestroy(): void {
    this.requestGeneration++;
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  refresh(): void {
    const clientId = this.clientService.client?.id;
    const generation = ++this.requestGeneration;
    this.status = null;
    this.error = null;
    if (!clientId) {
      this.error = 'Select a client to check D&B usage.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.dnbService.getUsageStatus(clientId).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (status) => {
        if (!this.isCurrentRequest(clientId, generation)) return;
        this.status = status;
        this.isLoading = false;
      },
      error: (error) => {
        if (!this.isCurrentRequest(clientId, generation)) return;
        this.diagnostics.reportError('enrich.dnb-usage.load', error);
        this.error = 'D&B usage is temporarily unavailable.';
        this.isLoading = false;
      },
    });
  }

  comparisonLabel(comparison: DnbUsageStatus['comparison']): string {
    if (comparison === 'aligned') return 'Aligned';
    if (comparison === 'different') return 'Review difference';
    return 'Comparison unavailable';
  }

  private isCurrentRequest(clientId: string, generation: number): boolean {
    return generation === this.requestGeneration && this.clientService.client?.id === clientId;
  }
}
